import { ComponentProps, useId } from "react";

import { TransformTool } from "@/apollo/types";
import { BetaFeatureText } from "@/components/elements/FeatureStateText";
import { type AppAction, WithRole, useRole } from "@/features/roleAccess";
import cn from "@/helpers/classNames";
import { useDataWarehouseContext } from "@/providers/DataWarehouseProvider";
import { useCurrentAccount } from "@/providers/account";
import { LinkWithSlug } from "@/router";
import { useFlags } from "launchdarkly-react-client-sdk";

export type SettingsNavItem = {
  label: string;
  to: string;
  hidden?: boolean;
  beta?: boolean;
  /** Shown disabled, with a tooltip, to users who lack it. */
  requires?: AppAction;
};

export type SettingsNavGroup = {
  label: string;
  items: SettingsNavItem[];
};

const DATA_WAREHOUSES_WITH_SETTINGS = [
  "snowflake",
  "weld-bigquery",
  "bigquery",
  "postgresql",
];

function useSettingsNavGroups(): SettingsNavGroup[] {
  const { can } = useRole();
  const dwh = useDataWarehouseContext();
  const account = useCurrentAccount();
  const { dataAccessControlEnabled } = useFlags();

  const dwhId = dwh.integration.id;
  const dwhHasSettings =
    DATA_WAREHOUSES_WITH_SETTINGS.includes(dwhId) || dwhId.includes("redshift");

  return [
    {
      label: "Data",
      items: [
        { label: "Connectors", to: "/settings/connections" },
        {
          label: "Data Warehouse",
          to: "/settings/data-warehouse",
          hidden: !dwhHasSettings,
          requires: "updateDataWarehouse",
        },
        {
          label: "ELT Destinations",
          to: "/settings/elt-destinations",
          beta: true,
        },
        { label: "Transform", to: "/settings/transform" },
        {
          label: "GitHub",
          to: "/settings/github",
          hidden:
            account.accountSettings.transformTool !== TransformTool.Github,
        },
        { label: "Import / Export SQL", to: "/settings/download" },
      ],
    },
    {
      label: "Workspace",
      items: [
        { label: "Members", to: "/settings/members" },
        {
          label: "Access groups",
          to: "/settings/access-groups",
          hidden: !dataAccessControlEnabled || !can("manageAccessGroups"),
        },
        { label: "Notifications", to: "/settings/notifications" },
        {
          label: "Audit Logs",
          to: "/settings/audit-logs",
          requires: "viewAuditLogs",
        },
        {
          label: "Account",
          to: "/settings/account",
          hidden: !can("deleteAccount"),
        },
      ],
    },
    {
      label: "Billing",
      items: [
        {
          label: can("manageBilling")
            ? "Plan Details & Billing"
            : "Plan Details",
          to: "/settings/plan",
        },
        { label: "Usage", to: "/settings/usage" },
      ],
    },
    {
      label: "AI",
      items: [
        { label: "Data sharing", to: "/settings/ai" },
        { label: "MCP Server", to: "/settings/mcp-server" },
      ],
    },
    {
      label: "Developer",
      items: [
        {
          label: "API Keys",
          to: "/settings/api-keys",
          requires: "manageApiKeys",
        },
      ],
    },
    {
      label: "Personal",
      items: [{ label: "User Profile", to: "/settings/user-profile" }],
    },
  ];
}

function MenuItemLink(props: ComponentProps<typeof LinkWithSlug>) {
  return (
    <LinkWithSlug
      {...props}
      className={cn(
        "block rounded-md px-3 py-1.5 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary",
        {
          "cursor-not-allowed opacity-50": props.disabled,
        },
        props.className,
      )}
      getActiveProps={() => ({
        "aria-current": "page",
        className: "bg-black/10 font-medium text-foreground dark:bg-white/10",
      })}
      getInactiveProps={() => ({
        className: "text-foreground/90 hover:bg-black/5 dark:hover:bg-white/5",
      })}
    />
  );
}

function SettingsNavLink({ item }: { item: SettingsNavItem }) {
  const link = (
    <MenuItemLink to={item.to}>
      {item.label}
      {item.beta && (
        <>
          {" "}
          <BetaFeatureText />
        </>
      )}
    </MenuItemLink>
  );
  if (!item.requires) {
    return link;
  }
  return (
    <WithRole action={item.requires} wrapperClassName="block">
      {link}
    </WithRole>
  );
}

function SettingsNavSection({ group }: { group: SettingsNavGroup }) {
  const labelId = useId();
  return (
    <div role="group" aria-labelledby={labelId}>
      <div
        id={labelId}
        className="px-3 pb-1.5 text-[11px] font-semibold tracking-[0.06em] text-muted-foreground uppercase"
      >
        {group.label}
      </div>
      <ul className="flex flex-col gap-px">
        {group.items.map((item) => (
          <li key={item.to}>
            <SettingsNavLink item={item} />
          </li>
        ))}
      </ul>
    </div>
  );
}

export function SettingsNav(props: ComponentProps<"nav">) {
  const groups = useSettingsNavGroups()
    .map((group) => ({
      ...group,
      items: group.items.filter((item) => !item.hidden),
    }))
    .filter((group) => group.items.length > 0);

  return (
    <nav
      aria-label="Settings"
      {...props}
      className={cn("flex flex-col gap-4.5 text-sm", props.className)}
    >
      {groups.map((group) => (
        <SettingsNavSection key={group.label} group={group} />
      ))}
    </nav>
  );
}
